import React, { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { EllipsisVertical, type LucideIcon } from 'lucide-react';
import { Button } from '@ui/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@ui/components/ui/dropdown-menu';
import { useIsMobile } from '@ui/hooks/use-mobile';
import { mainSprintAction, SprintAction } from './sprintActions';

/** How one report action reads and what it does, whether it is drawn as a button or as a menu entry. */
export interface SprintActionSpec {
  label: string;
  Icon: LucideIcon;
  onClick: () => void;
  /** Open to the reader, but not the step the report is waiting for. */
  outline?: boolean;
  loading?: boolean;
}

/** A report's actions: a row of buttons, or on a phone the main one with the rest under a menu beside it. */
export const SprintActionButtons = ({
  actions,
  specFor,
}: {
  actions: SprintAction[];
  specFor: Record<SprintAction, SprintActionSpec>;
}): ReactElement => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  const button = (action: SprintAction): ReactElement => {
    const { label, Icon, onClick, outline, loading } = specFor[action];
    return (
      <Button
        key={action}
        variant={outline ? 'outline' : undefined}
        className="inline-flex items-center"
        onClick={onClick}
        loading={loading}
      >
        {label}
        <Icon />
      </Button>
    );
  };

  const main = mainSprintAction(actions);
  if (!isMobile || actions.length < 2 || !main) return <>{actions.map(button)}</>;

  return (
    <div className="flex items-center gap-2">
      {button(main)}
      <DropdownMenu>
        <DropdownMenuTrigger
          render={
            <Button variant="outline" size="icon" aria-label={t('a11y.moreActions')}>
              <EllipsisVertical />
            </Button>
          }
        />
        <DropdownMenuContent align="end" className="w-auto min-w-44">
          {actions
            .filter(action => action !== main)
            .map(action => {
              const { label, Icon, onClick, loading } = specFor[action];
              return (
                <DropdownMenuItem key={action} onClick={onClick} disabled={loading}>
                  <Icon />
                  {label}
                </DropdownMenuItem>
              );
            })}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
